<template>
  <div class="departures-board">
    <!-- Header: airport, leads, controls -->
    <div class="list-group-item board-header">
      <div class="d-flex justify-content-between align-items-center gap-2">
        <strong>{{ airport.location }}</strong>
        <button class="btn btn-sm btn-outline-secondary" @click="$emit('change-airport')">{{ dict.change }}</button>
      </div>
      <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
        <select v-if="dates.length > 1" v-model="date" class="form-select form-select-sm w-auto">
          <option v-for="d in dates" :key="d" :value="d">{{ d }}</option>
        </select>
        <span v-else-if="date" class="text-muted small">{{ date }}</span>
        <div class="btn-group btn-group-sm">
          <button v-for="d in ['outbound', 'inbound']" :key="d" class="btn"
                  :class="direction === d ? 'btn-primary' : 'btn-outline-primary'" @click="direction = d">
            {{ d === 'outbound' ? dict.outbound : dict.inbound }}
          </button>
        </div>
        <label class="form-check form-switch m-0 ms-auto small">
          <input v-model="showDeparted" class="form-check-input" type="checkbox">
          <span class="form-check-label">{{ t.showDeparted }}</span>
        </label>
      </div>
      <!-- Airport-level people: leads and loading reps belong to the airport, not to each transfer -->
      <div v-for="group in leadGroups" :key="group.key" class="small mt-1" :data-test="`airport-${group.key}`">
        <span class="text-muted">{{ group.label }}:</span>
        <a v-for="person in group.people" :key="person.repId" href="#" class="ms-1"
           @click.prevent="callPerson(person)">{{ person.name || person.repId }}</a>
      </div>
    </div>

    <!-- Undo the change just made (TL-01, 60 s) -->

    <div v-if="error" class="list-group-item text-danger small">{{ error }}</div>

    <div v-if="loading && !runs.length" class="list-group-item text-center py-4" data-test="board-loading">
      <div class="spinner-border spinner-border-sm text-muted" role="status"></div>
    </div>
    <div v-else-if="loading" class="board-progress" data-test="board-refreshing"><div class="board-progress-bar"></div></div>

    <!-- Runs -->
    <div v-for="run in runs" :key="run.runId" class="list-group-item board-row"
         :class="{'row-overdue': run.loadingOverdue, 'board-stale': loading}">
      <div class="d-flex align-items-start gap-2">
        <router-link :to="loaderLink(run)" class="flex-fill text-reset text-decoration-none" @click="markSeen(run)">
          <div class="d-flex align-items-center gap-2 flex-wrap">
            <strong>{{ run.transferRef }}</strong>
            <TransferStatusBadge :status="run.status"/>
            <span v-if="isUnseen(run)" class="badge bg-red badge-blink" data-test="unseen" :title="t.statusChanged"></span>
          </div>
          <div class="small">
            <span :class="{'text-decoration-line-through text-muted': run.revisedTime}">{{ run.loadingTime }}</span>
            <strong v-if="run.revisedTime" class="text-danger ms-1">{{ run.revisedTime }}</strong>
            <small v-if="run.revisedTime && revisedDayNote(run.revisedAt, run.loadingDate)" class="text-danger ms-1"
                   data-test="revised-day">{{ revisedDayNote(run.revisedAt, run.loadingDate) }}</small>
            <span class="ms-2">{{ run.paxLoaded + run.externalLoaded }} / {{ run.pax + run.externalPax }} {{ t.loaded.toLowerCase() }}</span>
          </div>
          <div v-if="run.status === 'loading' && run.loadingStartedAt" class="small"
               :class="run.loadingOverdue ? 'text-warning fw-bold' : 'text-muted'" data-test="countdown">
            {{ t.loadingFor }} {{ minutesSince(run.loadingStartedAt) }} / {{ run.loadingWindowMinutes }} {{ t.minutes }}
          </div>

          <!-- Landscape: the doc's full list -->
          <div class="landscape-only small text-muted mt-1" data-test="landscape-details">
            <span>{{ t.departure }}: {{ run.liveDepartedAt ? clock(run.liveDepartedAt) : '—' }}</span>
            <span class="ms-2">{{ t.route }}: {{ run.airport }} → {{ run.resorts.map(r => r.name).join(', ') }}</span>
            <span class="ms-2">{{ t.capacity }}: {{ run.capacity ?? '—' }}</span>
            <span class="ms-2">{{ t.spare }}: {{ run.spareSeats ?? '—' }}</span>
            <span v-if="splitText(run)" class="ms-2" data-test="pax-split">{{ splitText(run) }}</span>
            <span class="ms-2">{{ t.staff }}: {{ run.staffCount }}</span>
          </div>
        </router-link>
        <button v-if="boardStatuses(run).length" class="btn btn-sm btn-outline-primary" data-test="set-status"
                @click="openSheet(run)">{{ t.setStatus }}</button>
      </div>
    </div>
    <div v-if="!loading && !runs.length" class="list-group-item text-center py-3 text-muted">{{ t.noRuns }}</div>

    <!-- Status bottom sheet -->
    <div v-if="sheet" class="sheet-backdrop" @click.self="sheet = null">
      <div class="sheet card">
        <div class="card-header"><strong>{{ sheet.transferRef }}</strong></div>
        <div class="list-group list-group-flush">
          <button v-for="s in boardStatuses(sheet)" :key="s" class="list-group-item list-group-item-action"
                  :class="{active: s === sheet.status}" :disabled="saving" @click="setStatus(sheet, s)">
            {{ statusLabel(s) }}
          </button>
        </div>
      </div>
    </div>

    <PhoneNumberActionSheet v-if="phone" :phone-number="phone" :is-visible="!!phone" @close="phone = null"/>
  </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref, watch} from 'vue'
import {api} from '@/services/api.js'
import PhoneNumberActionSheet from '@/components/PhoneNumberActionSheet.vue'
import TransferStatusBadge, {transferStatusLabel} from '@/components/TransferStatusBadge.vue'
import {revisedDayNote} from '@/utils/revisedDay.js'

const props = defineProps({
  weekId: {type: [String, Number], required: true},
  airport: {type: Object, required: true}
})
defineEmits(['change-airport'])

const FALLBACK = {
  statuses: {
    wait_in_airport: 'Wait in airport', delayed: 'Delayed', loading_imminent: 'Loading imminent',
    ready_to_load: 'Ready to load', loading: 'Loading', ready_to_depart: 'Ready to depart',
    permission_to_depart: 'Permission to depart', departed: 'Departed', arrived: 'Arrived', cancelled: 'Cancelled'
  },
  roles: {paxLead: 'Airport / Pax Lead', areaLead: 'Loading Area Lead', areaRep: 'Loading Area Rep', loadingReps: 'Loading reps', travellingReps: 'Travelling staff'},
  setStatus: 'Set status', statusChanged: 'Status changed', undo: 'Undo', showDeparted: 'Show departed',
  noRuns: 'No transfers load from this airport on this date', loaded: 'Loaded', capacity: 'Capacity', spare: 'Spare',
  departure: 'Departure', route: 'Route', staff: 'Staff', loadingFor: 'Loading for',
  minutes: 'min', statusFailed: 'Status not changed'
}
const dict = typeof __DICT__ !== 'undefined' ? __DICT__ : {}
const board = dict.departuresBoard || {}
const t = {...FALLBACK, ...board, statuses: {...FALLBACK.statuses, ...(board.statuses || {})}, roles: {...FALLBACK.roles, ...(board.roles || {})}}

const statusLabel = (s) => t.statuses[s] || transferStatusLabel(s)

const SHOW_DEPARTED_KEY = 'departuresBoard.showDeparted'
const readShowDeparted = () => { try { return localStorage.getItem(SHOW_DEPARTED_KEY) === '1' } catch { return false } }

const runs = ref([])
const dates = ref([])
const date = ref('')
const direction = ref('outbound')
const roles = ref({paxLeads: [], areaLeads: [], areaReps: []})
const isAdmin = ref(false)
const showDeparted = ref(readShowDeparted())
const loading = ref(false)
const error = ref('')
const saving = ref(false)
const sheet = ref(null)
const phone = ref(null)

// Loading reps of every run shown, once each, for the airport header.
const airportLoadingReps = computed(() => {
  const people = new Map()
  for (const run of runs.value) {
    for (const r of run.loadingReps || []) {
      const key = String(r.repId)
      if (!people.has(key)) people.set(key, {repId: r.repId, name: r.repName, phone: r.phone})
    }
  }
  return [...people.values()]
})

const leadGroups = computed(() => [
  {key: 'pax', label: t.roles.paxLead, people: roles.value.paxLeads},
  {key: 'area', label: t.roles.areaLead, people: roles.value.areaLeads},
  {key: 'areaRep', label: t.roles.areaRep, people: roles.value.areaReps},
  {key: 'loadingReps', label: t.roles.loadingReps, people: airportLoadingReps.value}
].filter(g => g.people.length))

// Reps change a transfer to Ready to load only, and only from Wait in airport or
// Loading imminent at level 3. The API enforces it and sends only that status;
// this keeps any other status out of the sheet for a rep.
const REP_BOARD_STATUSES = ['ready_to_load']
const boardStatuses = (run) => {
  const allowed = run?.canSetStatus || []
  return isAdmin.value ? allowed : allowed.filter(s => REP_BOARD_STATUSES.includes(s))
}

// Each load gets a sequence number so a slow poll that lands after a filter
// change cannot overwrite the newer board.
let loadSeq = 0
async function load({quiet = false} = {}) {
  const seq = ++loadSeq
  if (!quiet) loading.value = true
  try {
    const query = new URLSearchParams({direction: direction.value})
    if (date.value) query.set('date', date.value)
    if (showDeparted.value) query.set('showDeparted', '1')
    const data = await api.get(`/api/airport/board/${props.weekId}/${props.airport.systemId}?${query}`)
    if (seq !== loadSeq) return
    runs.value = data.runs || []
    dates.value = data.dates || []
    roles.value = data.roles || {paxLeads: [], areaLeads: [], areaReps: []}
    isAdmin.value = !!data.isAdmin
    if (!date.value && data.date) date.value = data.date
    error.value = ''
  } catch (e) {
    if (seq === loadSeq) error.value = e.message
  } finally {
    if (seq === loadSeq) loading.value = false
  }
}

watch(showDeparted, (on) => {
  try { localStorage.setItem(SHOW_DEPARTED_KEY, on ? '1' : '0') } catch { /* private mode */ }
  load()
})
watch(direction, () => { date.value = ''; load() })
// The direction watcher clears the date; only a real date change loads here.
watch(date, (value, old) => { if (old && value) load() })

function loaderLink(run) {
  const weekId = run.resortWeekIds?.map(String).includes(String(props.weekId)) ? props.weekId : (run.resortWeekIds?.[0] ?? props.weekId)
  return {path: `/loadTransferNew/${run.transferRef}/${run.direction}`, query: {weekId}}
}

function openSheet(run) {
  markSeen(run)
  sheet.value = run
}

// Unread indicator (TL-08): push and SMS are best-effort, so a status change
// the rep has not looked at yet is flagged on the row. Per device.
const SEEN_KEY = 'departuresBoard.seen'
const seen = ref((() => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') } catch { return {} } })())
let firstLoad = true
function isUnseen(run) {
  return run.statusAt != null && seen.value[run.runId] != null && run.statusAt > seen.value[run.runId]
}
function markSeen(run) {
  seen.value = {...seen.value, [run.runId]: run.statusAt || 0}
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(seen.value)) } catch { /* private mode */ }
}
// Runs seen for the first time are baselined, so only later changes flag.
watch(runs, (list) => {
  let changed = false
  const next = {...seen.value}
  for (const run of list) {
    if (next[run.runId] == null) { next[run.runId] = run.statusAt || 0; changed = true }
  }
  if (changed || firstLoad) {
    seen.value = next
    try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)) } catch { /* private mode */ }
  }
  firstLoad = false
})

// --- Undo ------------------------------------------------------------------

async function setStatus(run, status) {
  if (status === run.status) { sheet.value = null; return }
  saving.value = true
  try {
    await api.post('/api/transfer/status', {transferRef: run.transferRef, weekId: props.weekId, direction: run.direction, status})
    sheet.value = null
    // PWA changes take effect at once: no undo here (only admin has the 60 s window).
    await load()
  } catch (e) {
    error.value = `${t.statusFailed}: ${e.message}`
  } finally {
    saving.value = false
  }
}

// --- Helpers ----------------------------------------------------------------
function splitText(run) {
  return Object.entries(run.paxSplit || {}).map(([k, v]) => `${k} ${v.loaded}/${v.expected}`).join(', ')
}
function callPerson(person) {
  if (person.phone) phone.value = person.phone
}
const clock = (s) => new Date(s * 1000).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
const minutesSince = (s) => Math.max(0, Math.floor((Date.now() / 1000 - s) / 60))

// Poll every 15 s while the page is visible, like the loader.
let poll = null
const startPolling = () => { clearInterval(poll); poll = setInterval(() => { if (!document.hidden) load({quiet: true}) }, 15000) }

onMounted(() => {
  load()
  startPolling()
})
onUnmounted(() => {
  clearInterval(poll)
})

defineExpose({load, setStatus, statusLabel, boardStatuses})
</script>

<style scoped>
.board-header {
  background-color: rgba(var(--tblr-body-color-rgb), 0.04);
}
.badge-blink {
  width: 0.5rem;
  height: 0.5rem;
  padding: 0;
  border-radius: 50%;
}
.board-stale {
  opacity: 0.5;
  transition: opacity 0.15s;
}
.board-progress {
  height: 2px;
  overflow: hidden;
  background: rgba(var(--tblr-primary-rgb), 0.15);
}
.board-progress-bar {
  width: 30%;
  height: 100%;
  background: var(--tblr-primary);
  animation: board-progress 1s ease-in-out infinite;
}
@keyframes board-progress {
  from { transform: translateX(-100%); }
  to { transform: translateX(350%); }
}
.row-overdue {
  border-left: 4px solid var(--tblr-warning);
}
.landscape-only {
  display: none;
}
@media (orientation: landscape) {
  .landscape-only {
    display: block;
  }
}
.sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: flex-end;
}
.sheet {
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 0.75rem 0.75rem 0 0;
  margin: 0;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
</style>
